import { useCallback, useEffect, useRef, useState, type ChangeEvent, type Dispatch, type SetStateAction, type TextareaHTMLAttributes } from 'react'
import { InputFrame, type InputFrameProps } from '../layout/InputFrame'
import { useDebounce } from '@/hooks/useDebounce'
import { cn } from '@/utilities/misc'

export type TextAreaProps =
  Omit<InputFrameProps, 'prefix' | 'suffix' | 'children'> &
  TextareaHTMLAttributes<HTMLTextAreaElement> &
  {
    autoHeight?: boolean
    monospace?: boolean
    setValue?: Dispatch<SetStateAction<string>>,
    frameClassName?: string
  }

export function TextArea({
  label,
  tooltip,
  tooltipPlacement,
  required,
  disabled,
  width = '100%',
  height,
  autoHeight,
  monospace,
  className,
  frameClassName,
  children,
  onChange,
  setValue,
  value,
  ...rest
}: TextAreaProps) {

  const [observedWidth, setObservedWidth] = useState<number | null>(null)

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const mirrorRef = useRef<HTMLDivElement>(null)
  const debouncedWidth = useDebounce(observedWidth, 300)

  //--------------------------------------------------------------------------------
  className = cn(
    'TextArea',
    monospace && 'Monospace',
    autoHeight && 'AutoHeight',
    className,
  )

  //--------------------------------------------------------------------------------
  const adjustHeight = useCallback(() => {
    if (!autoHeight || !inputRef.current || !mirrorRef.current) return
    const el = inputRef.current
    const mirror = mirrorRef.current
    const style = getComputedStyle(el)
    const fontSize = parseFloat(style.fontSize)
    const lineHeightPx = style.lineHeight === 'normal'
      ? fontSize * 1.2
      : parseFloat(style.lineHeight)

    // Copy styles that affect layout so the mirror wraps like the textarea
    mirror.style.font = style.font
    mirror.style.width = `${el.clientWidth}px`
    mirror.style.padding = style.padding
    mirror.style.boxSizing = style.boxSizing
    mirror.style.lineHeight = style.lineHeight
    mirror.style.whiteSpace = style.whiteSpace
    mirror.style.overflowWrap = style.overflowWrap

    let text = value !== undefined ? String(value) : el.value
    if (text.endsWith('\n')) text += '.'
    mirror.textContent = text

    // getBoundingClientRect() forces reflow so mirror layout is up to date
    const contentHeight = mirror.getBoundingClientRect().height
    el.style.height = `${contentHeight + lineHeightPx}px`
  }, [autoHeight, value])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    adjustHeight()
  }, [adjustHeight, value])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    if (!autoHeight || !inputRef.current) return
    const el = inputRef.current
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setObservedWidth(entry.contentRect.width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [autoHeight])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    if (debouncedWidth !== null) adjustHeight()
  }, [debouncedWidth, adjustHeight])

  //--------------------------------------------------------------------------------
  const handleChange = useCallback((ev: ChangeEvent<HTMLTextAreaElement>) => {
    if (onChange)
      onChange(ev)
    if (setValue)
      setValue(ev.target.value)
    adjustHeight()
  }, [setValue, onChange, adjustHeight])

  //--------------------------------------------------------------------------------
  return (
    <InputFrame
      label={label}
      tooltip={tooltip}
      disabled={disabled}
      tooltipPlacement={tooltipPlacement}
      required={required}
      width={width}
      height={autoHeight ? undefined : height}
      className={cn('TextArea', frameClassName)}
    >
      <div style={autoHeight ? { position: 'relative' } : undefined}>
        {autoHeight && (
          <div
            ref={mirrorRef}
            aria-hidden='true'
            className='HiddenMirror'
          />
        )}
        <textarea
          ref={inputRef}
          className={className}
          disabled={disabled}
          value={value}
          onChange={handleChange}
          {...rest}
        />
      </div>
    </InputFrame>
  )
}
